import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  SquaresFour,
  ChartBar,
  Star,
  Trophy,
  Lightning,
  Warning,
} from '@phosphor-icons/react';

/**
 * Windows Navigation Menu — extracted from Dashboard.js (W11/W12/W21/W64 etc).
 * Pure-render: only depends on `useNavigate`.
 */
export default function WindowsNavMenu() {
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="windows-menu">
          <SquaresFour size={18} />
          Windows
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[80vh] overflow-y-auto w-72">
        {/* === PRIMARY === */}
        <DropdownMenuItem onClick={() => navigate('/')} className="gap-2 font-bold" data-testid="nav-raw-data">
          <SquaresFour size={16} />
          W1: Raw Data
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/consolidated-verdict')} className="gap-2 bg-gradient-to-r from-emerald-50 to-teal-50 font-bold text-emerald-900 border border-emerald-200 rounded my-1" data-testid="nav-consolidated-verdict">
          <ChartBar size={16} className="text-emerald-600" />
          W62: Consolidated Verdict 🎯
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window64')} className="gap-2 bg-gradient-to-r from-purple-50 to-pink-50 font-bold text-purple-900 border border-purple-200 rounded my-1" data-testid="nav-confluence">
          <ChartBar size={16} className="text-purple-700" />
          W64: Confluence Finder ✨
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/summary')} className="gap-2 bg-gradient-to-r from-amber-50 to-orange-50 font-bold text-amber-900 border border-amber-200 rounded my-1" data-testid="nav-summary">
          <ChartBar size={16} className="text-amber-600" />
          W41: Summary Dashboard
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/weights')} className="gap-2 bg-gradient-to-r from-indigo-50 to-blue-50 font-bold text-indigo-900 border border-indigo-200 rounded my-1" data-testid="nav-weights">
          <ChartBar size={16} className="text-indigo-600" />
          W42: Weights & Principles
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/my-prediction-maker')} className="gap-2 bg-gradient-to-r from-fuchsia-50 to-violet-50 font-bold text-fuchsia-900 border border-fuchsia-200 rounded my-1" data-testid="nav-my-prediction-maker">
          <Lightning size={16} className="text-fuchsia-600" />
          W44: My Prediction Maker
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/winner-data')} className="gap-2 bg-gradient-to-r from-amber-50 to-yellow-50 font-bold text-amber-900 border border-amber-200 rounded my-1" data-testid="nav-winner-data">
          <ChartBar size={16} className="text-amber-600" />
          W45: Winner Data
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/rebound-alert')} className="gap-2 bg-gradient-to-r from-red-50 to-orange-50 font-bold text-red-900 border border-red-200 rounded my-1" data-testid="nav-rebound-alert">
          <Warning size={16} className="text-red-500" />
          W46: Rebound & Reversion Engine 🔥🧊
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/my-picks')} className="gap-2 bg-gradient-to-r from-rose-50 to-pink-50 font-bold text-rose-900 border border-rose-200 rounded my-1" data-testid="nav-my-picks">
          <ChartBar size={16} className="text-rose-500" />
          W47: My Picks
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/prediction-checker')} className="gap-2 bg-gradient-to-r from-blue-50 to-cyan-50 font-bold text-blue-900 border border-blue-200 rounded my-1" data-testid="nav-prediction-checker">
          <ChartBar size={16} className="text-blue-600" />
          W48: Prediction Checker
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/prediction-maker')} className="gap-2 bg-gradient-to-r from-violet-50 to-purple-50 font-bold text-violet-900 border border-violet-200 rounded my-1" data-testid="nav-prediction-maker">
          <ChartBar size={16} className="text-violet-600" />
          W49: Prediction Maker
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window-accuracy')} className="gap-2 bg-gradient-to-r from-emerald-50 to-teal-50 font-bold text-emerald-900 border border-emerald-200 rounded my-1" data-testid="nav-window-accuracy">
          <ChartBar size={16} className="text-emerald-600" />
          W50: Window Accuracy Tracker
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/draw-validation')} className="gap-2 bg-gradient-to-r from-teal-50 to-emerald-50 font-bold text-teal-900 border border-teal-200 rounded my-1" data-testid="nav-draw-validation">
          <Trophy size={16} className="text-teal-600" />
          W52: Draw Validation Tracker
        </DropdownMenuItem>
        {/* === CRITICAL === */}
        <div className="px-2 pt-2 pb-1"><span className="text-[10px] font-bold text-red-600 uppercase tracking-wider">Critical</span></div>
        <DropdownMenuItem onClick={() => navigate('/pattern-predictor')} className="gap-2 bg-gradient-to-r from-orange-50 to-red-50 font-bold text-orange-900 border border-orange-200 rounded my-1" data-testid="nav-pattern-predictor">
          <ChartBar size={16} /> W54: Pattern Predictor
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window28')} className="gap-2">
          <ChartBar size={16} /> W28: Combination Validator
        </DropdownMenuItem>

        {/* === BALL 1 TO BALL 5 — Per-ball analysis windows (to be consolidated) === */}
        <div className="px-2 pt-2 pb-1"><span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Ball 1 to Ball 5</span></div>
        <DropdownMenuItem onClick={() => navigate('/master-prediction')} className="gap-2 bg-gradient-to-r from-purple-50 to-indigo-50 font-bold text-purple-900 border border-purple-200 rounded my-1" data-testid="nav-master-prediction">
          <ChartBar size={16} /> W53: Number Prediction: B1-B5 (Master)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window65')} className="gap-2 bg-gradient-to-r from-fuchsia-50 to-purple-50 font-bold text-fuchsia-900 border border-fuchsia-200 rounded my-1" data-testid="nav-w65">
          <Lightning size={16} className="text-fuchsia-600" /> W65: Method 2 (B1-B5)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window37')} className="gap-2 bg-gradient-to-r from-orange-50 to-rose-50 font-bold text-amber-900 border border-amber-200 rounded my-1" data-testid="nav-w37">
          <Lightning size={16} className="text-amber-600" /> W37: Yoga Poses ✨
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window6')} className="gap-2" data-testid="nav-w6">
          <ChartBar size={16} /> W6: Monthly &amp; Annual Frequency
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window7')} className="gap-2" data-testid="nav-w7">
          <ChartBar size={16} /> W7: Number Appearance (H/M/L)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window8')} className="gap-2" data-testid="nav-w8">
          <ChartBar size={16} /> W8: Before / After
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window11')} className="gap-2" data-testid="nav-w11">
          <ChartBar size={16} /> W11: Positional Hot/Cold
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window13')} className="gap-2" data-testid="nav-w13">
          <ChartBar size={16} /> W13: Gap Analysis
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window18')} className="gap-2" data-testid="nav-w18">
          <ChartBar size={16} /> W18: Rolling Window
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window19')} className="gap-2" data-testid="nav-w19">
          <ChartBar size={16} /> W19: Markov Chain
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window21')} className="gap-2" data-testid="nav-w21">
          <ChartBar size={16} /> W21: Decay Frequency
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window22')} className="gap-2">
          <ChartBar size={16} /> W22: Bayesian Posterior
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window23')} className="gap-2" data-testid="nav-w23">
          <ChartBar size={16} /> W23: Entropy
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window27')} className="gap-2" data-testid="nav-w27">
          <ChartBar size={16} /> W27: Outlier Detection
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window29')} className="gap-2" data-testid="nav-w29">
          <ChartBar size={16} /> W29: Digit Position
        </DropdownMenuItem>

        {/* === PATTERN WINDOWS === */}
        <div className="px-2 pt-2 pb-1"><span className="text-[10px] font-bold text-fuchsia-600 uppercase tracking-wider">Pattern Windows</span></div>
        <DropdownMenuItem onClick={() => navigate('/window3')} className="gap-2">
          <ChartBar size={16} /> W3 (A-X): Per-Ball Difference
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window3-pattern')} className="gap-2">
          <ChartBar size={16} /> W3.1 (A-X): Pattern Predictor
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window3-2')} className="gap-2">
          <ChartBar size={16} /> W3.2 (A-X) (All 7 Balls): 7-Combo
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window3-3')} className="gap-2" data-testid="nav-w3-3">
          <ChartBar size={16} /> W3.3 (5 Balls): UP/DWN Pattern
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window14')} className="gap-2" data-testid="nav-w14">
          <ChartBar size={16} /> W14: Sum Range (B1+B2+B3+B4+B5)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window24')} className="gap-2">
          <ChartBar size={16} /> W24: Ball Position (1-1-1-1-1)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window17')} className="gap-2">
          <ChartBar size={16} /> W17: Comparison to Previous Draws
        </DropdownMenuItem>
        {/* === VERY HIGH === */}
        <div className="px-2 pt-2 pb-1"><span className="text-[10px] font-bold text-orange-500 uppercase tracking-wider">Very High</span></div>
        <DropdownMenuItem onClick={() => navigate('/digit-affinity')} className="gap-2 bg-gradient-to-r from-purple-50 to-violet-50 font-bold text-purple-900 border border-purple-200 rounded my-1" data-testid="nav-digit-affinity">
          <ChartBar size={16} className="text-purple-600" />
          W51: Digit Affinity Analysis
        </DropdownMenuItem>
        {/* W30 Jackpot Cycle merged into W7 → Cycle Phase tab (Feb 2026) */}
        {/* === HIGH === (promoted from Medium; empty High + Medium-High headers removed Feb 2026) */}
        <div className="px-2 pt-2 pb-1"><span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider">High</span></div>
        <DropdownMenuItem onClick={() => navigate('/class-mix')} className="gap-2" data-testid="nav-class-mix">
          <ChartBar size={16} /> W55: Class Mix Projections
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/number-tier-consensus')} className="gap-2" data-testid="nav-tier-consensus">
          <ChartBar size={16} /> W56: Number Tier Consensus (GYR)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window16')} className="gap-2">
          <ChartBar size={16} /> W16: Pair Correlation
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/adjacent-pairs')} className="gap-2" data-testid="nav-adjacent-pairs">
          <ChartBar size={16} /> W66: Adjacent Ball Pairs
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/types')} className="gap-2" data-testid="nav-types-126">
          <ChartBar size={16} /> W67: Types (126 Decade-Bucket Patterns)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window20')} className="gap-2">
          <ChartBar size={16} /> W20: Spread & Distribution
        </DropdownMenuItem>
        {/* === LS 1 & LS 2 === */}
        <div className="px-2 pt-3 pb-1 border-t border-amber-200 mt-1"><span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">LS 1 & LS 2</span></div>
        <DropdownMenuItem onClick={() => navigate('/ls-prediction')} className="gap-2 bg-gradient-to-r from-amber-50 to-yellow-50 font-bold text-amber-900 border border-amber-200 rounded my-1" data-testid="nav-ls-prediction">
          <Star size={16} className="text-amber-500" weight="fill" /> W33: LS Prediction: LS1 & LS2
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window9')} className="gap-2">
          <ChartBar size={16} /> W34: LS Difference
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window10')} className="gap-2">
          <ChartBar size={16} /> W35: LS Appearance & Pairs
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window43')} className="gap-2">
          <ChartBar size={16} /> W36: LS2 - LS1 Spread
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/ls-advanced')} className="gap-2 bg-gradient-to-r from-indigo-50 to-purple-50 font-bold text-indigo-900 border border-indigo-200 rounded my-1" data-testid="nav-ls-advanced">
          <Star size={16} className="text-amber-500" /> W37-40: LS Advanced Analytics
        </DropdownMenuItem>
        {/* === LOW === */}
        <div className="px-2 pt-2 pb-1"><span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Low</span></div>
        {/* === SIGNAL === */}
        <div className="px-2 pt-2 pb-1"><span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Signal</span></div>
        <DropdownMenuItem onClick={() => navigate('/signals')} className="gap-2 bg-gradient-to-r from-amber-50 to-yellow-50 font-bold text-amber-900 border border-amber-200 rounded my-1" data-testid="nav-signals">
          <ChartBar size={16} className="text-amber-600" /> W58: Signals
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/ball-diff')} className="gap-2">
          <ChartBar size={16} /> W59: Analysis B2-B1
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window32')} className="gap-2">
          <ChartBar size={16} /> W32: Prime Analysis
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window25')} className="gap-2">
          <ChartBar size={16} /> W25: Number Connections
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window12')} className="gap-2">
          <ChartBar size={16} /> W12: Consecutive Pairs
        </DropdownMenuItem>
        {/* === TESTING WINDOWS === */}
        <div className="px-2 pt-2 pb-1"><span className="text-[10px] font-bold text-violet-600 uppercase tracking-wider">Testing Windows</span></div>
        <DropdownMenuItem onClick={() => navigate('/draw-dna')} className="gap-2">
          <ChartBar size={16} /> W60: Draw DNA Sequence Match
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/dna-validation')} className="gap-2">
          <ChartBar size={16} /> W61: DNA Edge Validation
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/window23')} className="gap-2">
          <ChartBar size={16} /> W23: Entropy & Randomness
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
